import { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import NavbarAdmin from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../components/Toast";
import { t } from "../i18n";
import { ReceiptDocument, ScaledPreview, openPrintWindow, downloadReceiptPdf, fmtDate, fmtDateThai } from "./ReceiptDocument";

const API = process.env.REACT_APP_API_URL || "";
const getToken = () => localStorage.getItem("token");
const authHeader = () => ({ Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" });

const inputStyle = {
  width: "100%", padding: "10px 14px", borderRadius: 8,
  border: "1px solid #d1d5db", fontSize: 14, background: "#fff",
  color: "#111827", boxSizing: "border-box", outline: "none", fontFamily: "inherit",
};
const labelStyle = { fontSize: 13, color: "#374151", display: "block", marginBottom: 6, fontWeight: 400 };
const cardStyle  = { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "20px 24px" };
const btnDark    = { padding: "11px", borderRadius: 8, border: "none", background: "#1e3a5f", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500 };
const btnGhost   = { padding: "10px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13, color: "#374151", fontWeight: 500 };

/* ─────────── HotelListPopup ─────────── */
function HotelListPopup({ onPick, onClose, showToast, lang }) {
  const [hotels,     setHotels]     = useState([]);
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [form,       setForm]       = useState({ name:"", nameEn:"", address:"", taxId:"", signerName:"", logo:"", signature:"", bankInfo:"", bankAccountName:"" });
  const [saving,     setSaving]     = useState(false);
  const [logoPreview,      setLogoPreview]      = useState("");
  const [signaturePreview, setSignaturePreview] = useState("");

  const fetchHotels = async (q = "") => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/profiles?type=hotel${q ? `&q=${encodeURIComponent(q)}` : ""}`, { headers: authHeader() });
      const data = await res.json();
      setHotels(Array.isArray(data) ? data : []);
    } catch { setHotels([]); }
    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchHotels(search), 280);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openNew = () => {
    setForm({ name:"", nameEn:"", address:"", taxId:"", signerName:"", logo:"", signature:"", bankInfo:"", bankAccountName:"" });
    setLogoPreview("");
    setSignaturePreview("");
    setEditTarget({});
  };

  const openEdit = h => {
    setForm({ name: h.name||"", nameEn: h.nameEn||"", address: h.address||"", taxId: h.taxId||"", signerName: h.signerName||"", logo: h.logo||"",
              signature: h.signature||"", bankInfo: h.bankInfo||"", bankAccountName: h.bankAccountName||"" });
    setLogoPreview(h.logo || "");
    setSignaturePreview(h.signature || "");
    setEditTarget(h);
  };

  const handleLogoUpload = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = ev => { setLogoPreview(ev.target.result); setForm(p => ({...p, logo: ev.target.result})); };
    r.readAsDataURL(file);
  };

  const handleSignatureUpload = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = ev => { setSignaturePreview(ev.target.result); setForm(p => ({...p, signature: ev.target.result})); };
    r.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showToast(t(lang, "reToastHotelRequired")); return; }
    setSaving(true);
    try {
      const isNew = !editTarget._id;
      const url    = isNew ? `${API}/api/profiles` : `${API}/api/profiles/${editTarget._id}`;
      const method = isNew ? "POST" : "PUT";
      const body   = { ...form, logo: logoPreview, signature: signaturePreview, type: "hotel" };
      const res    = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      showToast(isNew ? t(lang, "reToastHotelCreated") : t(lang, "reToastHotelSaved"));
      /* auto-pick hotel ที่เพิ่งบันทึก แล้วปิด popup */
      onPick({ ...body, _id: saved._id || editTarget._id });
      onClose();
    } catch { showToast(t(lang, "reToastError")); }
    setSaving(false);
  };

  const handleDelete = async h => {
    if (!window.confirm(`${t(lang, "reConfirmDeleteHotel")} "${h.name}" ?`)) return;
    try {
      await fetch(`${API}/api/profiles/${h._id}`, { method:"DELETE", headers: authHeader() });
      showToast(t(lang, "reToastHotelSaved"));
      fetchHotels(search);
    } catch { showToast(t(lang, "reToastError")); }
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1100,
               display:"flex", alignItems:"center", justifyContent:"center", padding:"12px" }}>
      <div style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:860,
                    height:"90vh", display:"flex", flexDirection:"column",
                    boxShadow:"0 8px 32px rgba(0,0,0,0.18)", overflow:"hidden" }}>

        {/* header */}
        <div style={{ padding:"18px 24px 14px", borderBottom:"1px solid #e5e7eb",
                      display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <p style={{ margin:0, fontWeight:600, fontSize:15, color:"#111827" }}>{t(lang, "reHotelList")}</p>
            <p style={{ margin:"2px 0 0", fontSize:12, color:"#6b7280" }}>{t(lang, "reHotelListSub")}</p>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={openNew} style={{ ...btnDark, padding:"8px 18px", fontSize:13 }}>{t(lang, "reCreate")}</button>
            <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:22, lineHeight:1 }}>✕</button>
          </div>
        </div>

        {/* search */}
        <div style={{ padding:"12px 24px 8px" }}>
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t(lang, "reSearchHotelPh")} style={inputStyle} />
        </div>

        {/* list */}
        <div style={{ flex: editTarget?._id ? "0 0 auto" : editTarget ? 0 : 1, overflowY:"auto", padding:"4px 24px 16px", display:"flex", flexDirection:"column", gap:4 }}>
          {loading && <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"24px 0" }}>{t(lang, "reLoading")}</p>}
          {!loading && hotels.length === 0 && !editTarget && <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"24px 0" }}>{t(lang, "reNoHotel")}</p>}
          {(!editTarget ? hotels : editTarget._id ? hotels.filter(h => h._id === editTarget._id) : []).map(h => (
            <div key={h._id}
              style={{ display:"flex", alignItems:"center", gap:12, border:"1px solid #e5e7eb", borderRadius:8, padding:"12px 16px", background:"#fff" }}>
              {h.logo && (
                <div style={{ width:40, height:40, borderRadius:"50%", border:"2px solid #dbeafe", overflow:"hidden", flexShrink:0 }}>
                  <img src={h.logo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                </div>
              )}
              <div style={{ flex:1, minWidth:0, cursor:"pointer" }} onClick={() => { onPick(h); onClose(); }}
                onMouseEnter={e => e.currentTarget.parentElement.style.background="#f0f9ff"}
                onMouseLeave={e => e.currentTarget.parentElement.style.background="#fff"}>
                <p style={{ margin:0, fontSize:13, fontWeight:600, color:"#111827" }}>{h.name}</p>
                {h.taxId   && <p style={{ margin:"2px 0 0", fontSize:12, color:"#6b7280" }}>Tax ID: {h.taxId}</p>}
                {h.address && <p style={{ margin:"2px 0 0", fontSize:12, color:"#9ca3af" }}>{h.address}</p>}
              </div>
              <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                <button onClick={() => openEdit(h)}
                  style={{ background:"#eff6ff", border:"none", borderRadius:6, padding:"6px 12px", cursor:"pointer", fontSize:12, color:"#2563eb", fontWeight:500 }}>{t(lang, "reEditHotel")}</button>
                <button onClick={() => handleDelete(h)}
                  style={{ background:"#fef2f2", border:"none", borderRadius:6, padding:"6px 12px", cursor:"pointer", fontSize:12, color:"#dc2626", fontWeight:500 }}>{t(lang, "reDeleteHotel")}</button>
              </div>
            </div>
          ))}
        </div>

        {/* edit/create form */}
        {editTarget !== null && (
          <div style={{ borderTop:"1px solid #e5e7eb", padding:"16px 24px", background:"#f9fafb",
                        overflowY:"auto", maxHeight:"55vh" }}>
            <p style={{ margin:"0 0 12px", fontSize:13, fontWeight:600, color:"#111827" }}>
              {editTarget._id ? t(lang, "reEditHotelTitle") : t(lang, "reNewHotelTitle")}
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:10 }}>
              <div>
                <label style={labelStyle}>{t(lang, "reHotelName")}</label>
                <input value={form.name} onChange={e => setForm(p => ({...p, name:e.target.value}))}
                  placeholder={t(lang, "reHotelNamePh")} style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "reHotelNameEn")}</label>
                <input value={form.nameEn} onChange={e => setForm(p => ({...p, nameEn:e.target.value}))}
                  placeholder="Hotel Name (English)" style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "reTaxId")}</label>
                <input value={form.taxId} onChange={e => setForm(p => ({...p, taxId:e.target.value}))}
                  placeholder="0000000000000" style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={labelStyle}>{t(lang, "reAddress")}</label>
                <input value={form.address} onChange={e => setForm(p => ({...p, address:e.target.value}))}
                  placeholder={t(lang, "reHotelAddrPh")} style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "reSigner")}</label>
                <input value={form.signerName} onChange={e => setForm(p => ({...p, signerName:e.target.value}))}
                  placeholder={t(lang, "reSignerPh")} style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "reLogo")}</label>
                <label style={{ ...inputStyle, display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, color:"#6b7280" }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="" style={{ width:28, height:28, borderRadius:"50%", objectFit:"cover" }} />
                    : <span>📷</span>
                  }
                  <span>{logoPreview ? t(lang, "reChangeLogo") : t(lang, "reUploadLogo")}</span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display:"none" }} />
                </label>
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "reSignature")}</label>
                <label style={{ ...inputStyle, display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, color:"#6b7280" }}>
                  {signaturePreview
                    ? <img src={signaturePreview} alt="" style={{ height:28, maxWidth:80, objectFit:"contain" }} />
                    : <span>✍️</span>
                  }
                  <span>{signaturePreview ? t(lang, "reChangeSign") : t(lang, "reUploadSign")}</span>
                  <input type="file" accept="image/*" onChange={handleSignatureUpload} style={{ display:"none" }} />
                </label>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={labelStyle}>{t(lang, "reBankInfo")}</label>
                <input value={form.bankInfo} onChange={e => setForm(p => ({...p, bankInfo:e.target.value}))}
                  placeholder={t(lang, "reBankInfoPh")} style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={labelStyle}>{t(lang, "reBankName")}</label>
                <input value={form.bankAccountName} onChange={e => setForm(p => ({...p, bankAccountName:e.target.value}))}
                  placeholder={t(lang, "reBankNamePh")} style={{ ...inputStyle, fontSize:13 }} />
              </div>
            </div>
            <div style={{ display:"flex", gap:8, marginTop:12 }}>
              <button onClick={() => { setEditTarget(null); setLogoPreview(""); }} style={{ ...btnGhost, flex:1 }}>{t(lang, "reCancelBtn")}</button>
              <button onClick={handleSave} disabled={saving} style={{ ...btnDark, flex:2, opacity: saving ? 0.7 : 1 }}>
                {saving ? t(lang, "reSaving") : t(lang, "reSave")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── CustomerListPopup ─────────── */
function CustomerListPopup({ onPick, onClose, showToast, lang }) {
  const [customers,   setCustomers]   = useState([]);
  const [search,      setSearch]      = useState("");
  const [loading,     setLoading]     = useState(true);
  const [editTarget,  setEditTarget]  = useState(null); // null | {} (new) | {_id,...} (edit)
  const [form,        setForm]        = useState({ name: "", taxId: "", address: "", branchType: "head", branchNo: "" });
  const [saving,      setSaving]      = useState(false);

  const fetchCustomers = async (q = "") => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/profiles?type=customer${q ? `&q=${encodeURIComponent(q)}` : ""}`, { headers: authHeader() });
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch { setCustomers([]); }
    setLoading(false);
  };

  /* debounce search — fetchCustomers excluded from deps intentionally (stable per render) */
  useEffect(() => {
    const timer = setTimeout(() => fetchCustomers(search), 280);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openNew = () => {
    setForm({ name: "", taxId: "", address: "", branchType: "head", branchNo: "" });
    setEditTarget({});
  };

  const openEdit = (c) => {
    setForm({ name: c.name || "", taxId: c.taxId || "", address: c.address || "",
              branchType: c.branchType === "branch" ? "branch" : "head", branchNo: c.branchNo || "" });
    setEditTarget(c);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showToast(t(lang, "reToastCustRequired")); return; }
    setSaving(true);
    try {
      const isNew = !editTarget._id;
      const url    = isNew ? `${API}/api/profiles` : `${API}/api/profiles/${editTarget._id}`;
      const method = isNew ? "POST" : "PUT";
      const res    = await fetch(url, { method, headers: authHeader(), body: JSON.stringify({ ...form, type: "customer" }) });
      if (!res.ok) throw new Error();
      showToast(isNew ? t(lang, "reToastCustCreated") : t(lang, "reToastCustSaved"));
      setEditTarget(null);
      fetchCustomers(search);
    } catch { showToast(t(lang, "reToastError")); }
    setSaving(false);
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`${t(lang, "reConfirmDeleteCustomer")} "${c.name}" ?`)) return;
    try {
      await fetch(`${API}/api/profiles/${c._id}`, { method: "DELETE", headers: authHeader() });
      showToast(t(lang, "reToastCustSaved"));
      fetchCustomers(search);
    } catch { showToast(t(lang, "reToastError")); }
  };

  const handlePick = (c) => {
    onPick(c);
    onClose();
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1100,
               display:"flex", alignItems:"center", justifyContent:"center", padding:"12px" }}
    >
      <div style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:860,
                    height:"90vh", display:"flex", flexDirection:"column",
                    boxShadow:"0 8px 32px rgba(0,0,0,0.18)", overflow:"hidden" }}>

        {/* header */}
        <div style={{ padding:"18px 24px 14px", borderBottom:"1px solid #e5e7eb",
                      display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <p style={{ margin:0, fontWeight:600, fontSize:15, color:"#111827" }}>{t(lang, "reCustomerList")}</p>
            <p style={{ margin:"2px 0 0", fontSize:12, color:"#6b7280" }}>{t(lang, "reCustomerListSub")}</p>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={openNew} style={{ ...btnDark, padding:"8px 18px", fontSize:13 }}>{t(lang, "reCreate")}</button>
            <button onClick={onClose}
              style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:22, lineHeight:1 }}>✕</button>
          </div>
        </div>

        {/* search */}
        <div style={{ padding:"12px 24px 8px" }}>
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t(lang, "reSearchCustomerPh")} style={inputStyle} />
        </div>

        {/* list */}
        <div style={{ flex: editTarget?._id ? "0 0 auto" : editTarget ? 0 : 1, overflowY:"auto", padding:"4px 24px 16px",
                      display:"flex", flexDirection:"column", gap:4 }}>
          {loading && (
            <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"24px 0" }}>{t(lang, "reLoading")}</p>
          )}
          {!loading && customers.length === 0 && !editTarget && (
            <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"24px 0" }}>{t(lang, "reNoHotel")}</p>
          )}
          {(!editTarget ? customers : editTarget._id ? customers.filter(c => c._id === editTarget._id) : []).map(c => (
            <div key={c._id}
              style={{ display:"flex", alignItems:"center", gap:12,
                       border:"1px solid #e5e7eb", borderRadius:8,
                       padding:"12px 16px", background:"#fff" }}>
              {/* info — clickable */}
              <div style={{ flex:1, minWidth:0, cursor:"pointer" }} onClick={() => handlePick(c)}
                onMouseEnter={e => e.currentTarget.parentElement.style.background="#f0f9ff"}
                onMouseLeave={e => e.currentTarget.parentElement.style.background="#fff"}>
                <p style={{ margin:0, fontSize:13, fontWeight:600, color:"#111827" }}>{c.name}</p>
                {c.taxId    && <p style={{ margin:"2px 0 0", fontSize:12, color:"#6b7280" }}>Tax ID: {c.taxId}</p>}
                {c.address  && <p style={{ margin:"2px 0 0", fontSize:12, color:"#9ca3af" }}>{c.address}</p>}
              </div>
              {/* actions */}
              <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                <button onClick={() => openEdit(c)}
                  style={{ background:"#eff6ff", border:"none", borderRadius:6,
                           padding:"6px 12px", cursor:"pointer", fontSize:12, color:"#2563eb", fontWeight:500 }}>
                  {t(lang, "reEditHotel")}
                </button>
                <button onClick={() => handleDelete(c)}
                  style={{ background:"#fef2f2", border:"none", borderRadius:6,
                           padding:"6px 12px", cursor:"pointer", fontSize:12, color:"#dc2626", fontWeight:500 }}>
                  {t(lang, "reDeleteHotel")}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* edit / create form — slide up inside popup */}
        {editTarget !== null && (
          <div style={{ borderTop:"1px solid #e5e7eb", padding:"16px 24px", background:"#f9fafb",
                        overflowY:"auto", maxHeight:"55vh" }}>
            <p style={{ margin:"0 0 12px", fontSize:13, fontWeight:600, color:"#111827" }}>
              {editTarget._id ? t(lang, "reEditCustomer") : t(lang, "reNewCustomer")}
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:10 }}>
              <div>
                <label style={labelStyle}>{t(lang, "reCustomerNameLbl")}</label>
                <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
                  onKeyDown={e => e.stopPropagation()} autoComplete="off"
                  placeholder={t(lang, "reCustomerNamePh")} style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div>
                <label style={labelStyle}>{t(lang, "reTaxId")}</label>
                <input value={form.taxId} onChange={e => setForm(p => ({...p, taxId: e.target.value}))}
                  placeholder="0123456789123" style={{ ...inputStyle, fontSize:13 }} />
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={labelStyle}>{t(lang, "reAddress")}</label>
                <input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))}
                  placeholder={t(lang, "reCustomerAddrLbl")} style={{ ...inputStyle, fontSize:13 }} />
              </div>
              {/* สำนักงานใหญ่ / สาขา */}
              <div style={{ gridColumn:"1/-1" }}>
                <label style={labelStyle}>{t(lang, "reBranchType")}</label>
                <div style={{ display:"flex", gap:8 }}>
                  {[["head", t(lang, "reHeadOffice")], ["branch", t(lang, "reBranch")]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setForm(p => ({ ...p, branchType: val }))}
                      style={{ flex:1, padding:"9px 12px", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:500,
                               border: form.branchType === val ? "1.5px solid #1e3a5f" : "1px solid #d1d5db",
                               background: form.branchType === val ? "#eef2f7" : "#fff",
                               color: form.branchType === val ? "#1e3a5f" : "#374151" }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {form.branchType === "branch" && (
                <div>
                  <label style={labelStyle}>{t(lang, "reBranchNo")}</label>
                  <input value={form.branchNo} onChange={e => setForm(p => ({...p, branchNo: e.target.value}))}
                    placeholder={t(lang, "reBranchNoPh")} style={{ ...inputStyle, fontSize:13 }} />
                </div>
              )}
            </div>
            <div style={{ display:"flex", gap:8, marginTop:12 }}>
              <button onClick={() => setEditTarget(null)} style={{ ...btnGhost, flex:1 }}>{t(lang, "reCancelBtn")}</button>
              <button onClick={handleSave} disabled={saving} style={{ ...btnDark, flex:2, opacity: saving ? 0.7 : 1 }}>
                {saving ? t(lang, "reSaving") : t(lang, "reSave")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── Main ─────────── */
export default function ReceiptEditor() {
  const { lang } = useLanguage();
  const printRef = useRef(null);
  const [tab, setTab] = useState("hotel");

  /* hotel */
  const [hotelInfo,        setHotelInfo]        = useState({ name:"", nameEn:"", address:"", taxId:"", signerName:"", logo:"", signature:"" });
  const [logoPreview,      setLogoPreview]       = useState("");
  const [signaturePreview, setSignaturePreview]  = useState("");
  const [showHotelList,    setShowHotelList]     = useState(false);

  /* customer fields */
  const [customerTaxId,   setCustomerTaxId]   = useState("");
  const [customerName,    setCustomerName]     = useState("");
  const [customerAddress, setCustomerAddress]  = useState("");
  const [customerBranchType, setCustomerBranchType] = useState("head"); // "head" | "branch"
  const [customerBranchNo,   setCustomerBranchNo]   = useState("");

  /* autocomplete */
  const [acQuery,           setAcQuery]           = useState("");
  const [acResults,         setAcResults]         = useState([]);
  const [showCustomerList,  setShowCustomerList]  = useState(false);

  /* receipt — เลขที่ออกโดย server ตอนบันทึก (พิมพ์/ดาวน์โหลด) เพื่อให้ unique ทุกเครื่อง */
  const [receiptNo,      setReceiptNo]      = useState("");
  const [savedReceiptId, setSavedReceiptId] = useState(null);
  const [printing,       setPrinting]       = useState(false);
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [includeVat,  setIncludeVat]  = useState(false);
  const [note,        setNote]        = useState("");
  const [bankInfo,        setBankInfo]        = useState("ชำระเงินผ่านทางบัญชี กสิกรไทย หมายเลขบัญชี 191-3-29323-8");
  const [bankAccountName, setBankAccountName] = useState("ชื่อบัญชี นายพงศธร สุดธาราภิรมย์");

  /* items + bookings */
  const [items,        setItems]        = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [popupSearch,  setPopupSearch]  = useState("");
  const [popupMode,    setPopupMode]    = useState("main");   // "main" | "custom"
  const [customForm,   setCustomForm]   = useState({ name:"", sub:"", qty:1, price:"" });
  const [itemSearch,   setItemSearch]   = useState("");

  /* toast */
  const [toast, setToast] = useState("");
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(""), 2400); };

  /* fetch bookings + hotel on mount */
  useEffect(() => {
    fetch(`${API}/api/bookings`, { headers: authHeader() })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setBookings(d); }).catch(() => {});
    fetch(`${API}/api/service-items`, { headers: authHeader() })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setServiceItems(d); }).catch(() => {});
    fetch(`${API}/api/profiles?type=hotel`, { headers: authHeader() })
      .then(r => r.json()).then(d => {
        if (Array.isArray(d) && d.length > 0) {
          setHotelInfo(d[0]); setLogoPreview(d[0].logo || ""); setSignaturePreview(d[0].signature || "");
          if (d[0].bankInfo)        setBankInfo(d[0].bankInfo);
          if (d[0].bankAccountName) setBankAccountName(d[0].bankAccountName);
        }
      }).catch(() => {});
  }, []);

  /* customer autocomplete — live search from API */
  useEffect(() => {
    if (!acQuery.trim()) { setAcResults([]); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API}/api/profiles?type=customer&q=${encodeURIComponent(acQuery)}`,
          { headers: authHeader(), signal: controller.signal }
        );
        const data = await res.json();
        setAcResults(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch { /* aborted or failed — ignore */ }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acQuery]);

const pickCustomer = c => {
    setCustomerName(c.name || "");
    setCustomerTaxId(c.taxId || "");
    setCustomerAddress(c.address || "");
    setCustomerBranchType(c.branchType === "branch" ? "branch" : "head");
    setCustomerBranchNo(c.branchNo || "");
    setAcQuery(""); setAcResults([]);
  };

  /* items */
  const removeItem = i => setItems(p => p.filter((_, idx) => idx !== i));
  const updateItem = (i, f, v) => setItems(p => p.map((it, idx) => idx === i ? { ...it, [f]: v } : it));

  /* รายละเอียดใต้ชื่อรายการ — เก็บเป็นสตริงเดียวคั่นด้วย \n เพื่อให้ snapshot เก่าที่มีบรรทัดเดียวใช้ได้เหมือนเดิม */
  const subLines = it => String(it.sub || "").split("\n");
  const updateSubLine = (i, li, v) => setItems(p => p.map((it, idx) => {
    if (idx !== i) return it;
    const lines = subLines(it); lines[li] = v;
    return { ...it, sub: lines.join("\n") };
  }));
  const addSubLine    = i      => setItems(p => p.map((it, idx) => idx === i ? { ...it, sub: `${it.sub || ""}\n` } : it));
  const removeSubLine = (i, li) => setItems(p => p.map((it, idx) =>
    idx === i ? { ...it, sub: subLines(it).filter((_, l) => l !== li).join("\n") } : it));

  /* logo / signature (main form) */
  const handleLogoUpload = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = ev => { setLogoPreview(ev.target.result); setHotelInfo(p => ({ ...p, logo: ev.target.result })); };
    r.readAsDataURL(file);
  };

  const handleMainSignatureUpload = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = ev => { setSignaturePreview(ev.target.result); setHotelInfo(p => ({ ...p, signature: ev.target.result })); };
    r.readAsDataURL(file);
  };


  /* แก้ไขข้อมูลใดๆ หลังบันทึกแล้ว → ถือเป็นใบใหม่ (พิมพ์ครั้งถัดไปจะได้เลขที่ใหม่)
     ถ้าไม่แก้อะไรเลย พิมพ์/ดาวน์โหลดซ้ำจะได้เลขที่เดิม */
  useEffect(() => {
    if (savedReceiptId) { setSavedReceiptId(null); setReceiptNo(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, includeVat, note, receiptDate, customerName, customerAddress, customerTaxId,
      customerBranchType, customerBranchNo, bankInfo, bankAccountName, hotelInfo, logoPreview, signaturePreview]);

  const closeBookingPopup = () => {
    setShowAddPopup(false);
    setPopupSearch("");
    setPopupMode("main");
    setCustomForm({ name:"", sub:"", qty:1, price:"" });
    setItemSearch("");
  };

  const addCustomItem = () => {
    if (!customForm.name.trim()) return;
    const price = Number(customForm.price);
    const qty   = Math.max(1, Number(customForm.qty) || 1);
    if (isNaN(price) || price < 0) return;
    setItems(p => [...p, { name: customForm.name.trim(), sub: customForm.sub.trim(), qty, price, paid: 0 }]);
    closeBookingPopup();
  };

  const receiptData = {
    hotelName: hotelInfo.name, hotelNameEn: hotelInfo.nameEn, hotelAddress: hotelInfo.address,
    hotelTaxId: hotelInfo.taxId, logo: logoPreview, signerName: hotelInfo.signerName, signature: signaturePreview,
    receiptNo, receiptDate, customerName, customerAddress, customerTaxId,
    customerBranchType, customerBranchNo,
    items, includeVat, note, bankInfo, bankAccountName,
  };

  /* บันทึกใบเสร็จลงคลัง (ครั้งแรก) หรือแจ้งนับพิมพ์ซ้ำ (ใบเดิม) → คืน { id, no } | null ถ้าล้มเหลว */
  const ensureSaved = async () => {
    if (savedReceiptId) {
      fetch(`${API}/api/receipts/${savedReceiptId}/reprint`, { method: "PATCH", headers: authHeader() }).catch(() => {});
      return { id: savedReceiptId, no: receiptNo };
    }
    const res = await fetch(`${API}/api/receipts`, {
      method: "POST", headers: authHeader(),
      body: JSON.stringify({ snapshot: receiptData }),
    });
    if (!res.ok) return null;
    const doc = await res.json();
    // flushSync เพื่อให้เอกสารใน printRef แสดงเลขที่จริงก่อนอ่าน innerHTML/แปลง PDF
    flushSync(() => { setReceiptNo(doc.receiptNo); setSavedReceiptId(doc._id); });
    return { id: doc._id, no: doc.receiptNo };
  };

  /* print — บันทึกก่อนเสมอ ถ้าบันทึกไม่สำเร็จจะไม่พิมพ์ (การันตีทุกใบอยู่ในคลัง) */
  const handlePrint = async () => {
    if (printing) return;
    if (items.length === 0) { showToast(t(lang, "reNeedItems")); return; }
    setPrinting(true);
    try {
      const saved = await ensureSaved();
      if (!saved) { showToast(t(lang, "reSaveReceiptFail")); return; }
      openPrintWindow(printRef.current?.innerHTML, `ใบเสร็จ ${saved.no}`);
    } catch { showToast(t(lang, "reSaveReceiptFail")); }
    finally { setPrinting(false); }
  };

  /* ดาวน์โหลดเป็นไฟล์ PDF (ต้นฉบับ + สำเนา 2 หน้า) — บันทึกลงคลังก่อนเช่นเดียวกับพิมพ์ */
  const handleDownloadPdf = async () => {
    if (printing) return;
    if (items.length === 0) { showToast(t(lang, "reNeedItems")); return; }
    setPrinting(true);
    try {
      const saved = await ensureSaved();
      if (!saved) { showToast(t(lang, "reSaveReceiptFail")); return; }
      await downloadReceiptPdf(printRef.current, `ใบเสร็จ_${saved.no}.pdf`);
    } catch { showToast(t(lang, "reSaveReceiptFail")); }
    finally { setPrinting(false); }
  };

  /* ─── RENDER ─── */
  return (
    <div style={{ minHeight:"100vh", background:"#f3f4f6", fontFamily:"'Noto Sans Thai', sans-serif" }}>
      <NavbarAdmin />

      {/* ── Responsive (screen) ── ยุบกริด editor 2 ฝั่งเป็นคอลัมน์เดียวบนจอแคบ */}
      <style>{`
        @media (max-width: 820px) {
          .re-grid-main, .re-grid-items { grid-template-columns: 1fr !important; }
          .re-main-pad { padding: 20px 16px !important; }
        }
      `}</style>

      {/* Toast */}
      <Toast show={!!toast} type="info" message={toast} lang={lang} />

      {/* Hotel List Popup */}
      {showHotelList && (
        <HotelListPopup
          onPick={h => {
            setHotelInfo(h); setLogoPreview(h.logo || ""); setSignaturePreview(h.signature || "");
            if (h.bankInfo)        setBankInfo(h.bankInfo);
            if (h.bankAccountName) setBankAccountName(h.bankAccountName);
            setShowHotelList(false);
          }}
          onClose={() => setShowHotelList(false)}
          showToast={showToast}
          lang={lang}
        />
      )}

      {/* Customer List Popup */}
      {showCustomerList && (
        <CustomerListPopup
          onPick={c => { pickCustomer(c); setShowCustomerList(false); }}
          onClose={() => setShowCustomerList(false)}
          showToast={showToast}
          lang={lang}
        />
      )}

      {/* Booking Popup */}
      {showAddPopup && (() => {
        const filtered = bookings
          .filter(b => b.status === "CheckedOut")
          .filter(b => {
            const q = popupSearch.toLowerCase(); if (!q) return true;
            return (
              b.roomType?.name?.toLowerCase().includes(q) ||
              b.user?.email?.toLowerCase().includes(q) ||
              b.user?.firstname?.toLowerCase().includes(q) ||
              b.user?.lastname?.toLowerCase().includes(q) ||
              b._id?.slice(-6).toLowerCase().includes(q) ||
              fmtDate(b.checkIn).includes(q)
            );
          });
        return (
          <div onClick={e => { if (e.target === e.currentTarget) closeBookingPopup(); }}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000,
                     display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ background:"#fff", borderRadius:16, width:"90vw", maxWidth:900, height:"88vh",
                          display:"flex", flexDirection:"column", boxShadow:"0 8px 32px rgba(0,0,0,0.18)" }}>
              <div style={{ padding:"18px 24px 14px", borderBottom:"1px solid #e5e7eb",
                            display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  {popupMode === "custom" && (
                    <button onClick={() => setPopupMode("main")}
                      style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"6px 14px",
                               borderRadius:8, border:"1px solid #e5e7eb", background:"#fff", cursor:"pointer",
                               fontSize:12.5, color:"#374151", fontWeight:500, fontFamily:"inherit" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                      </svg>
                      {t(lang, "reBack")}
                    </button>
                  )}
                  <div>
                    <p style={{ margin:0, fontWeight:600, fontSize:15, color:"#111827" }}>
                      {popupMode === "custom" ? t(lang, "reManualEntry") : t(lang, "reSelectBooking")}
                    </p>
                    <p style={{ margin:"2px 0 0", fontSize:12, color:"#6b7280" }}>
                      {popupMode === "custom" ? t(lang, "reManualEntrySub") : t(lang, "reSelectBookingSub")}
                    </p>
                  </div>
                </div>
                <button onClick={closeBookingPopup}
                  style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:20 }}>✕</button>
              </div>

              {popupMode === "custom" ? (
                /* ── หน้ากรอกรายการเอง ── */
                <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>

                  {/* ฟอร์มกรอกเอง — การ์ดด้านบน เรียบง่าย */}
                  <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px", marginBottom:20 }}>
                    <p style={{ margin:"0 0 12px", fontSize:13, fontWeight:600, color:"#111827" }}>{t(lang, "reManualForm")}</p>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:10, marginBottom:10 }}>
                      <div>
                        <label style={{ ...labelStyle, fontSize:12, marginBottom:4 }}>{t(lang, "reItemName")}</label>
                        <input autoFocus value={customForm.name}
                          onChange={e => setCustomForm(p => ({...p, name:e.target.value}))}
                          placeholder={t(lang, "reItemNamePh")} style={{ ...inputStyle, fontSize:13 }} />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize:12, marginBottom:4 }}>{t(lang, "reItemQty")}</label>
                        <input type="number" min={1} value={customForm.qty}
                          onChange={e => setCustomForm(p => ({...p, qty:e.target.value}))}
                          style={{ ...inputStyle, fontSize:13, textAlign:"center" }} />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize:12, marginBottom:4 }}>{t(lang, "reItemPrice")}</label>
                        <input type="number" min={0} value={customForm.price}
                          onChange={e => setCustomForm(p => ({...p, price:e.target.value}))}
                          placeholder="0.00" style={{ ...inputStyle, fontSize:13, textAlign:"right" }} />
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:10 }}>
                      <input value={customForm.sub}
                        onChange={e => setCustomForm(p => ({...p, sub:e.target.value}))}
                        placeholder={t(lang, "reItemDetail")} style={{ ...inputStyle, fontSize:13, flex:1 }} />
                      <button onClick={addCustomItem}
                        disabled={!customForm.name.trim() || customForm.price === ""}
                        style={{ ...btnDark, padding:"10px 26px", whiteSpace:"nowrap",
                                 opacity: (!customForm.name.trim() || customForm.price === "") ? 0.5 : 1 }}>
                        {t(lang, "reAddBtn")}
                      </button>
                    </div>
                  </div>

                  {/* รายการสินค้า/บริการ — อยู่ล่าง พร้อมค้นหา */}
                  <p style={{ margin:"0 0 8px", fontSize:13, fontWeight:600, color:"#111827" }}>{t(lang, "reServiceList")}</p>
                  <div style={{ position:"relative", marginBottom:10 }}>
                    <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:"#bbb", fontSize:13 }}>🔍</span>
                    <input value={itemSearch} onChange={e => setItemSearch(e.target.value)}
                      placeholder={t(lang, "reSearchItem")}
                      style={{ ...inputStyle, fontSize:13, paddingLeft:34 }} />
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {(() => {
                      const q = itemSearch.toLowerCase().trim();
                      const matched = serviceItems.filter(s => !q || s.name.toLowerCase().includes(q));
                      if (matched.length === 0) {
                        return (
                          <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"18px 0", margin:0 }}>
                            {serviceItems.length === 0 ? t(lang, "reNoSavedItems") : t(lang, "reNoSearchItem")}
                          </p>
                        );
                      }
                      return matched.map(s => (
                        <div key={s._id}
                          onClick={() => {
                            setItems(p => [...p, { name: s.name, sub: "", qty: 1, price: s.price, paid: 0 }]);
                            closeBookingPopup();
                          }}
                          style={{ padding:"11px 16px", borderRadius:8, cursor:"pointer",
                                   border:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}
                          onMouseEnter={e => e.currentTarget.style.background="#f0f9ff"}
                          onMouseLeave={e => e.currentTarget.style.background="#fff"}>
                          <p style={{ margin:0, fontSize:13, fontWeight:500, color:"#111827" }}>{s.name}</p>
                          <p style={{ margin:0, fontSize:13, fontWeight:600, color:"#111827", whiteSpace:"nowrap" }}>
                            {Number(s.price).toLocaleString("th-TH",{minimumFractionDigits:2})} THB
                          </p>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              ) : (
              <>
              <div style={{ padding:"12px 24px 8px" }}>
                <input autoFocus value={popupSearch} onChange={e => setPopupSearch(e.target.value)}
                  placeholder={t(lang, "reSearchBooking")} style={inputStyle} />
              </div>
              <div style={{ flex:1, overflowY:"auto", padding:"4px 24px 16px",
                            display:"flex", flexDirection:"column", gap:4 }}>
                <div onClick={() => setPopupMode("custom")}
                  style={{ padding:"12px 16px", borderRadius:8, cursor:"pointer",
                           border:"1.5px dashed #d1d5db", display:"flex", alignItems:"center", justifyContent:"space-between" }}
                  onMouseEnter={e => e.currentTarget.style.background="#f9fafb"}
                  onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                  <div>
                    <p style={{ margin:0, fontSize:13, fontWeight:500, color:"#374151" }}>{t(lang, "reManualEntry")}</p>
                    <p style={{ margin:"1px 0 0", fontSize:11, color:"#9ca3af" }}>{t(lang, "reManualEntrySub")}</p>
                  </div>
                  <span style={{ color:"#9ca3af", fontSize:16 }}>›</span>
                </div>

                {filtered.length === 0 && bookings.length > 0 && (
                  <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"24px 0" }}>{t(lang, "reNoMatchBooking")}</p>
                )}
                {filtered.length === 0 && bookings.length === 0 && (
                  <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"24px 0" }}>{t(lang, "reLoadingBooking")}</p>
                )}

                {filtered.map(b => {
                  const nights        = b.checkIn && b.checkOut
                    ? Math.max(1, Math.round((new Date(b.checkOut) - new Date(b.checkIn)) / 86400000)) : 1;
                  const total         = Number(b.totalAmount || 0);
                  const pricePerNight = total > 0 ? Math.round(total / nights) : 0;
                  const roomName      = b.roomType?.name || b.roomUnit?.roomNumber || "ห้องพัก";
                  const roomLabel     = `ค่าบริการ${roomName}`;
                  const subLabel      = `Check in: ${fmtDateThai(b.checkIn)}  Check out: ${fmtDateThai(b.checkOut)}`;
                  const guestName     = [b.user?.firstname, b.user?.lastname].filter(Boolean).join(" ") || b.user?.email || b.guestName || "";
                  const paidAmt       = Number(b.paidAmount || 0);
                  const alreadyAdded  = items.some(x => x._bookingId === b._id);

                  return (
                    <div key={b._id}
                      onClick={() => {
                        if (alreadyAdded) return;
                        setItems(p => [...p, { _bookingId:b._id, name:roomLabel, sub:subLabel, qty:nights, price:pricePerNight, paid:paidAmt }]);
                        if (guestName && !customerName) {
                          setCustomerName(guestName);
                          setCustomerAddress(b.user?.address || "");
                          setTab("customer");
                        }
                        closeBookingPopup();
                      }}
                      style={{ padding:"12px 16px", borderRadius:8, cursor:alreadyAdded?"default":"pointer",
                               border:"1px solid #e5e7eb", background:alreadyAdded?"#f9fafb":"#fff",
                               opacity:alreadyAdded?0.6:1, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}
                      onMouseEnter={e => { if (!alreadyAdded) e.currentTarget.style.background="#f0f9ff"; }}
                      onMouseLeave={e => { if (!alreadyAdded) e.currentTarget.style.background="#fff"; }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ margin:0, fontSize:13, fontWeight:500, color:"#111827" }}>{roomName}</p>
                        <p style={{ margin:"3px 0 0", fontSize:12, color:"#6b7280" }}>
                          {fmtDate(b.checkIn)} → {fmtDate(b.checkOut)} · {nights} {t(lang, "reNights")} · {pricePerNight.toLocaleString("th-TH")} THB
                        </p>
                        {guestName && <p style={{ margin:"2px 0 0", fontSize:12, color:"#9ca3af" }}>{guestName}</p>}
                      </div>
                      <div style={{ textAlign:"right", flexShrink:0 }}>
                        {alreadyAdded
                          ? <span style={{ fontSize:12, color:"#10b981", background:"#ecfdf5", padding:"3px 10px", borderRadius:100 }}>{t(lang, "reAlreadyAdded")}</span>
                          : <>
                              <p style={{ margin:0, fontSize:13, fontWeight:600, color:"#111827" }}>{total.toLocaleString("th-TH",{minimumFractionDigits:2})} THB</p>
                              <p style={{ margin:"2px 0 0", fontSize:11, color:"#6b7280" }}>{nights} {t(lang, "reNights")}</p>
                            </>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
              )}
            </div>
          </div>
        );
      })()}

      {/* MAIN */}
      <div className="re-main-pad" style={{ padding:"28px 32px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
          <span style={{ fontSize:20 }}>🖨</span>
          <h1 style={{ margin:0, fontSize:20, fontWeight:600, color:"#111827" }}>{t(lang, "reTitle")}</h1>
        </div>

        {/* Tab buttons */}
        <div style={{ display:"flex", gap:8, marginBottom:20 }}>
          {(["hotel","customer"] ).map(tabKey => (
            <button key={tabKey} onClick={() => setTab(tabKey)} style={{
              padding:"8px 20px", borderRadius:8, border:"none", cursor:"pointer",
              fontSize:13, fontWeight:500,
              background: tab === tabKey ? "#1e3a5f" : "#e5e7eb",
              color: tab === tabKey ? "#fff" : "#374151",
            }}>
              {tabKey === "hotel" ? t(lang, "reHotelTab") : t(lang, "reCustomerTab")}
            </button>
          ))}
        </div>

        <div className="re-grid-main" style={{ display:"grid", gridTemplateColumns:"380px 1fr", gap:24, alignItems:"start" }}>

          {/* LEFT PANEL */}
          <div>
            {tab === "hotel" ? (
              /* ── Hotel Tab ── */
              <div style={cardStyle}>
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

                  {/* hotel search button */}
                  <div>
                    <label style={labelStyle}>{t(lang, "reSearchHotel")}</label>
                    <button
                      onClick={() => setShowHotelList(true)}
                      style={{ ...btnGhost, width:"100%", display:"flex", alignItems:"center", gap:8, justifyContent:"center" }}>
                       {t(lang, "reSearchHotelBtn")}
                    </button>
                  </div>

                  {/* logo upload */}
                  <div>
                    <label style={labelStyle}>{t(lang, "reLogo")}</label>
                    <label style={{ ...inputStyle, display:"flex", alignItems:"center", gap:10, cursor:"pointer", color:"#6b7280" }}>
                      {logoPreview
                        ? <img src={logoPreview} alt="" style={{ width:32, height:32, borderRadius:"50%", objectFit:"cover" }} />
                        : <span>📷</span>
                      }
                      <span style={{ fontSize:13 }}>{logoPreview ? t(lang, "reChangeLogo") : t(lang, "reUploadLogo")}</span>
                      <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display:"none" }} />
                    </label>
                  </div>

                  <div>
                    <label style={labelStyle}>{t(lang, "reHotelName")}</label>
                    <input value={hotelInfo.name||""} onChange={e => setHotelInfo(p=>({...p,name:e.target.value}))}
                      placeholder={t(lang, "reHotelNamePh")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reHotelNameEn")}</label>
                    <input value={hotelInfo.nameEn||""} onChange={e => setHotelInfo(p=>({...p,nameEn:e.target.value}))}
                      placeholder="Hotel Name (English)" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reAddress")}</label>
                    <input value={hotelInfo.address||""} onChange={e => setHotelInfo(p=>({...p,address:e.target.value}))}
                      placeholder={t(lang, "reHotelAddrPh2")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reTaxId")}</label>
                    <input value={hotelInfo.taxId||""} onChange={e => setHotelInfo(p=>({...p,taxId:e.target.value}))}
                      placeholder={t(lang, "reTaxIdPh")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reSigner")}</label>
                    <input value={hotelInfo.signerName||""} onChange={e => setHotelInfo(p=>({...p,signerName:e.target.value}))}
                      placeholder={t(lang, "reSignerPh")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reSignature")}</label>
                    <label style={{ ...inputStyle, display:"flex", alignItems:"center", gap:10, cursor:"pointer", color:"#6b7280" }}>
                      {signaturePreview
                        ? <img src={signaturePreview} alt="" style={{ height:32, maxWidth:100, objectFit:"contain" }} />
                        : <span>✍️</span>
                      }
                      <span style={{ fontSize:13 }}>{signaturePreview ? t(lang, "reChangeSign") : t(lang, "reUploadSign")}</span>
                      <input type="file" accept="image/*" onChange={handleMainSignatureUpload} style={{ display:"none" }} />
                    </label>
                  </div>
                </div>

                <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:16 }}>
                  <div>
                    <label style={labelStyle}>{t(lang, "reReceiptDate")}</label>
                    <input type="date" value={receiptDate} onChange={e => setReceiptDate(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reNote")}</label>
                    <input value={note} onChange={e => setNote(e.target.value)} placeholder={t(lang, "reNotePh")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reBankInfo")}</label>
                    <input value={bankInfo} onChange={e => setBankInfo(e.target.value)}
                      placeholder={t(lang, "reBankInfoPh")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t(lang, "reBankName")}</label>
                    <input value={bankAccountName} onChange={e => setBankAccountName(e.target.value)}
                      placeholder={t(lang, "reBankNamePh")} style={inputStyle} />
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <input type="checkbox" id="vat" checked={includeVat} onChange={e => setIncludeVat(e.target.checked)}
                      style={{ width:16, height:16, cursor:"pointer" }} />
                    <label htmlFor="vat" style={{ fontSize:13, color:"#374151", cursor:"pointer" }}>{t(lang, "reIncludeVat")}</label>
                  </div>
                </div>

                <div style={{ marginTop:20, display:"flex", flexDirection:"column", gap:8 }}>
                  <button onClick={handlePrint} disabled={printing}
                    style={{ ...btnDark, width:"100%", opacity: printing ? 0.6 : 1 }}>
                    {printing ? t(lang, "reSaving") : t(lang, "rePrintOriginal")}
                  </button>
                  <button onClick={handleDownloadPdf} disabled={printing}
                    style={{ ...btnGhost, width:"100%", opacity: printing ? 0.6 : 1 }}>
                    {t(lang, "reDownloadPdf")}
                  </button>
                </div>
              </div>

            ) : (
              /* ── Customer Tab ── */
              <div style={cardStyle}>
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

                  {/* search with autocomplete + 🔍 popup button */}
                  <div>
                    <label style={labelStyle}>{t(lang, "reCustomerTax")}</label>
                    <div style={{ position:"relative" }}>
                      <input
                        value={acQuery}
                        onChange={e => setAcQuery(e.target.value)}
                        onBlur={() => setTimeout(() => setAcResults([]), 180)}
                        placeholder="0123456789123"
                        style={{ ...inputStyle, paddingRight: 44 }}
                      />
                      {/* 🔍 icon → open popup */}
                      <button
                        onMouseDown={e => { e.preventDefault(); setShowCustomerList(true); }}
                        style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)",
                                 background:"none", border:"none", cursor:"pointer", color:"#6b7280", fontSize:16, padding:4 }}
                      >🔍</button>

                      {/* autocomplete dropdown */}
                      {acResults.length > 0 && (
                        <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:200,
                                      background:"#fff", border:"1px solid #e5e7eb", borderRadius:8,
                                      boxShadow:"0 4px 16px rgba(0,0,0,0.12)", overflow:"hidden", marginTop:4 }}>
                          {acResults.map(c => (
                            <div key={c._id} onMouseDown={() => pickCustomer(c)}
                              style={{ padding:"10px 14px", cursor:"pointer", fontSize:13, borderBottom:"1px solid #f3f4f6" }}
                              onMouseEnter={e => e.currentTarget.style.background="#f0f9ff"}
                              onMouseLeave={e => e.currentTarget.style.background="#fff"}>
                              <div style={{ fontWeight:500, color:"#111827" }}>{c.name}</div>
                              {c.taxId && <div style={{ fontSize:11, color:"#9ca3af" }}>Tax ID: {c.taxId}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* tax id */}
                  <div>
                    <label style={labelStyle}>{t(lang, "reCustomerTax")}</label>
                    <input value={customerTaxId} onChange={e => setCustomerTaxId(e.target.value)}
                      placeholder="0123456789123" style={inputStyle} />
                  </div>

                  {/* name */}
                  <div>
                    <label style={labelStyle}>{t(lang, "reCustomerName")}</label>
                    <input
                      value={customerName}
                      onChange={e => setCustomerName(e.target.value)}
                      onKeyDown={e => e.stopPropagation()}
                      autoComplete="off"
                      placeholder={t(lang, "reCustomerNamePh2")}
                      style={inputStyle}
                    />
                  </div>

                  {/* address */}
                  <div>
                    <label style={labelStyle}>{t(lang, "reCustomerAddr")}</label>
                    <input value={customerAddress} onChange={e => setCustomerAddress(e.target.value)}
                      placeholder={t(lang, "reCustomerAddrPh")} style={inputStyle} />
                  </div>

                  {/* สำนักงานใหญ่ / สาขา */}
                  <div>
                    <label style={labelStyle}>{t(lang, "reBranchType")}</label>
                    <div style={{ display:"flex", gap:8 }}>
                      {[["head", t(lang, "reHeadOffice")], ["branch", t(lang, "reBranch")]].map(([val, label]) => (
                        <button key={val} type="button" onClick={() => setCustomerBranchType(val)}
                          style={{ flex:1, padding:"10px 12px", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:500,
                                   border: customerBranchType === val ? "1.5px solid #1e3a5f" : "1px solid #d1d5db",
                                   background: customerBranchType === val ? "#eef2f7" : "#fff",
                                   color: customerBranchType === val ? "#1e3a5f" : "#374151" }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {customerBranchType === "branch" && (
                    <div>
                      <label style={labelStyle}>{t(lang, "reBranchNo")}</label>
                      <input value={customerBranchNo} onChange={e => setCustomerBranchNo(e.target.value)}
                        placeholder={t(lang, "reBranchNoPh")} style={inputStyle} />
                    </div>
                  )}
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:10, marginTop:20 }}>
                  <button onClick={handleDownloadPdf} disabled={printing}
                    style={{ ...btnGhost, opacity: printing ? 0.6 : 1 }}>{t(lang, "reDownloadPdf")}</button>
                  <button onClick={handlePrint} disabled={printing}
                    style={{ ...btnDark, opacity: printing ? 0.6 : 1 }}>
                    {printing ? t(lang, "reSaving") : t(lang, "rePrintOriginal")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* CENTER + RIGHT */}
          <div className="re-grid-items" style={{ display:"grid", gridTemplateColumns:"1fr 390px", gap:24, alignItems:"start" }}>

            {/* Items panel */}
            <div style={cardStyle}>
              <p style={{ margin:"0 0 12px", fontSize:13, fontWeight:600, color:"#111827" }}>{t(lang, "reItems")}</p>
              {items.length === 0 && (
                <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"24px 0" }}>
                  {t(lang, "reAddItem")}
                </p>
              )}
              {items.map((item, idx) => (
                <div key={idx} style={{ padding:"12px 0", borderBottom:"1px solid #f3f4f6" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 72px 120px 36px", gap:8, alignItems:"start" }}>
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      <input value={item.name} onChange={e => updateItem(idx,"name",e.target.value)}
                        placeholder={t(lang, "reItemNamePh2")} style={{ ...inputStyle, fontSize:13, padding:"5px 8px" }} />
                      {subLines(item).map((line, li, arr) => (
                        <div key={li} style={{ display:"flex", gap:4, alignItems:"center" }}>
                          <input value={line} onChange={e => updateSubLine(idx, li, e.target.value)}
                            placeholder={t(lang, "reItemDetailPh")}
                            style={{ ...inputStyle, fontSize:11, padding:"4px 8px", color:"#6b7280" }} />
                          {arr.length > 1 && (
                            <button onClick={() => removeSubLine(idx, li)} title={t(lang, "reRemoveDetailLine")}
                              style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af",
                                       fontSize:14, lineHeight:1, padding:"0 4px", flexShrink:0 }}>✕</button>
                          )}
                        </div>
                      ))}
                      <button onClick={() => addSubLine(idx)}
                        style={{ alignSelf:"flex-start", background:"none", border:"none", cursor:"pointer",
                                 color:"#6b7280", fontSize:11, padding:"1px 0", fontFamily:"inherit" }}>
                        {t(lang, "reAddDetailLine")}
                      </button>
                    </div>
                    <input type="number" min={1} value={item.qty} onChange={e => updateItem(idx,"qty",e.target.value)}
                      style={{ ...inputStyle, textAlign:"center", fontSize:13, padding:"5px 8px" }} />
                    {item._bookingId
                      ? <div style={{ textAlign:"right", fontSize:13, fontWeight:500, color:"#111827", paddingTop:8 }}>
                          {Number((item.qty||1)*(item.price||0)).toLocaleString("th-TH",{minimumFractionDigits:2})}
                        </div>
                      : <input type="number" min={0} value={item.price} onChange={e => updateItem(idx,"price",e.target.value)}
                          style={{ ...inputStyle, textAlign:"right", fontSize:13, padding:"5px 8px" }} />
                    }
                    <button onClick={() => removeItem(idx)}
                      style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:6,
                               cursor:"pointer", color:"#dc2626", fontSize:16,
                               width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center",
                               marginTop:2, flexShrink:0 }}>🗑</button>
                  </div>
                  {item._bookingId && (
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6 }}>
                      <span style={{ fontSize:12, color:"#6b7280", whiteSpace:"nowrap" }}>{t(lang, "reAlreadyAdded")}:</span>
                      <input type="number" min={0} value={item.paid||0} onChange={e => updateItem(idx,"paid",e.target.value)}
                        style={{ ...inputStyle, fontSize:12, padding:"3px 8px", width:120, color:"#16a34a" }} />
                      <span style={{ fontSize:12, color:"#9ca3af" }}>THB</span>
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => setShowAddPopup(true)}
                style={{ marginTop:12, width:"100%", padding:"10px", borderRadius:8,
                         border:"1.5px dashed #d1d5db", background:"transparent", cursor:"pointer", fontSize:13, color:"#6b7280" }}>
                {t(lang, "reAddBtn")}
              </button>
            </div>

            {/* Preview */}
            <div>
              <p style={{ margin:"0 0 10px", fontSize:14, fontWeight:600, color:"#111827" }}>Preview</p>
              <ScaledPreview data={receiptData} />
            </div>
          </div>
        </div>
      </div>

      {/* Hidden print target — ต้นฉบับ (สำหรับลูกค้า) + สำเนา (สำหรับบริษัท) */}
      <div style={{ position:"absolute", left:-9999, top:-9999, pointerEvents:"none" }}>
        <div ref={printRef}>
          <ReceiptDocument data={receiptData} variant="original" />
          <div style={{ pageBreakBefore:"always" }} />
          <ReceiptDocument data={receiptData} variant="copy" />
        </div>
      </div>
    </div>
  );
}
